import { useState } from "react";
import { useForm, usePage, Link } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DateTimePopup from "@/components/app/DateTimePopup";
import LocationAutocomplete from "@/components/app/LocationAutocomplete";
import { ChevronLeft } from "lucide-react";

// ─── Date helpers ─────────────────────────────────────────────

// Any parseable date string or Date → Date | null
function toDate(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val) ? null : val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

// ─── New page ─────────────────────────────────────────────────
export default function EventNew() {
  const { routes, teams = [], errors: serverErrors = {} } = usePage().props;
  const form = useForm({
    title:         "",
    description:   "",
    location_name: "",
    address:       "",
    latitude:      "",
    longitude:     "",
    starts_at:     "",
    ends_at:       "",
    is_public:     true,
    player_limit:  "",
    team_id:       "",
  });

  const errors = { ...serverErrors, ...form.errors };

  function submit(e) {
    e.preventDefault();
    form.post(routes.events);
  }

  return (
    <AppLayout>
      <div className="container py-8 max-w-2xl mx-auto">
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-1 text-muted-foreground">
          <Link href={routes.events}>
            <ChevronLeft className="h-4 w-4" />
            Back to events
          </Link>
        </Button>

        <h1 className="text-2xl font-bold mb-6">Host an event</h1>

        <form onSubmit={submit} className="space-y-5">
          <EventFormFields form={form} errors={errors} teams={teams} />

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={form.processing}>
              {form.processing ? "Creating…" : "Create event"}
            </Button>
            <Button asChild variant="ghost">
              <Link href={routes.events}>Cancel</Link>
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

// ─── Shared form fields ───────────────────────────────────────
export function EventFormFields({ form, errors = {}, teams = [] }) {
  const [startsAt, setStartsAt_] = useState(() => toDate(form.data.starts_at));
  const [endsAt,   setEndsAt_]   = useState(() => toDate(form.data.ends_at));

  const [locationValue] = useState(() => {
    const { location_name, address } = form.data;
    return location_name || address || "";
  });

  function handleLocationSelect(feature) {
    const [lng, lat] = feature.center;
    form.setData({
      ...form.data,
      location_name: feature.place_name,
      address:       feature.place_name,
      latitude:      lat,
      longitude:     lng,
    });
  }

  function handleLocationChange(val) {
    if (!val.trim()) {
      form.setData({ ...form.data, location_name: "", address: "", latitude: "", longitude: "" });
    }
  }

  function handleStartsAtChange(date) {
    let newEndsAt = endsAt;
    // If the new start is at or after the current end, reset end to start + 3 h
    if (date && endsAt && date >= endsAt) {
      newEndsAt = new Date(date.getTime() + 3 * 60 * 60 * 1000);
      setEndsAt_(newEndsAt);
    }
    setStartsAt_(date);
    form.setData({
      ...form.data,
      starts_at: date?.toISOString() ?? "",
      ends_at:   newEndsAt?.toISOString() ?? "",
    });
  }

  function handleEndsAtChange(date) {
    setEndsAt_(date);
    form.setData({ ...form.data, ends_at: date?.toISOString() ?? "" });
  }

  // Default open position for the ends_at picker when no value is set
  const endsAtDefault = startsAt ? new Date(startsAt.getTime() + 3 * 60 * 60 * 1000) : null;

  // Live ends_at validation (also caught by Rails, but show immediately)
  const endsAtError =
    (endsAt && startsAt && endsAt <= startsAt)
      ? "End time must be after start time"
      : errors.ends_at;

  return (
    <>
      {/* Title */}
      <div className="space-y-1.5">
        <Label htmlFor="title">
          Event title <span className="text-destructive">*</span>
        </Label>
        <Input
          id="title"
          autoFocus
          placeholder="Tuesday night Catan"
          value={form.data.title}
          onChange={(e) => form.setData("title", e.target.value)}
        />
        {errors.title && <FieldError>{errors.title}</FieldError>}
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <Label htmlFor="description">Description</Label>
        <textarea
          id="description"
          rows={3}
          placeholder="What are you playing? Any house rules or things to bring?"
          value={form.data.description}
          onChange={(e) => form.setData("description", e.target.value)}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      {/* Location */}
      <div className="space-y-1.5">
        <Label htmlFor="location_name">Location</Label>
        <LocationAutocomplete
          value={locationValue}
          placeholder="Search by venue name or address…"
          onSelect={handleLocationSelect}
          onChange={handleLocationChange}
        />
        <p className="text-xs text-muted-foreground">
          Start typing a business name or street address. Results are biased toward your current location.
        </p>
      </div>

      {/* Starts at */}
      <div className="space-y-1.5">
        <Label htmlFor="event-starts-at">
          Starts at <span className="text-destructive">*</span>
        </Label>
        <DateTimePopup
          id="event-starts-at"
          value={startsAt}
          onChange={handleStartsAtChange}
          placeholder="Pick a start date & time"
          error={!!errors.starts_at}
        />
        {errors.starts_at && <FieldError>{errors.starts_at}</FieldError>}
      </div>

      {/* Ends at */}
      <div className="space-y-1.5">
        <Label htmlFor="event-ends-at">Ends at</Label>
        <DateTimePopup
          id="event-ends-at"
          value={endsAt}
          onChange={handleEndsAtChange}
          defaultValue={endsAtDefault}
          placeholder="Optional"
          error={!!endsAtError}
          clearable
        />
        {endsAtError && <FieldError>{endsAtError}</FieldError>}
      </div>

      {/* Player limit + Team */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="player_limit">Player limit</Label>
          <Input
            id="player_limit"
            type="number"
            min="1"
            placeholder="Leave blank for unlimited"
            value={form.data.player_limit}
            onChange={(e) => form.setData("player_limit", e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="team_id">Team (optional)</Label>
          <select
            id="team_id"
            value={form.data.team_id}
            onChange={(e) => form.setData("team_id", e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">No team</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Public / Private */}
      <div className="flex items-start gap-3 rounded-lg border border-border p-4">
        <input
          type="checkbox"
          id="is_public"
          checked={form.data.is_public}
          onChange={(e) => form.setData("is_public", e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
        />
        <div>
          <Label htmlFor="is_public" className="cursor-pointer">Public event</Label>
          <p className="text-muted-foreground text-xs mt-0.5">
            Listed in the public events feed. Uncheck to make it invite-only.
          </p>
        </div>
      </div>
    </>
  );
}

function FieldError({ children }) {
  return <p className="text-xs text-destructive">{children}</p>;
}
